import React from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { CheckCircle } from "@phosphor-icons/react";

export function Pricing() {
  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-16">
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-center" style={{ color: "var(--brand-green)" }}>Simple, fair pricing</h1>
        <p className="text-center text-[var(--text-muted)] mt-3 max-w-2xl mx-auto">Customers post jobs free, forever. Tradespeople pay just £3.99/month with no commissions, no per-lead charges, no nonsense.</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="brand-card p-8">
            <div className="text-xs font-semibold text-[var(--brand-orange)] uppercase tracking-wider">Customers</div>
            <div className="font-display font-extrabold text-5xl mt-1" style={{ color: "var(--brand-green)" }}>Free</div>
            <div className="text-[var(--text-muted)] text-sm">Forever — no fees, no upsells.</div>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Post unlimited jobs</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Get multiple quotes</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Built-in messenger</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Verified trades & reviews</li>
            </ul>
            <Link to="/register?role=customer" className="btn-ghost mt-6 inline-flex" data-testid="pricing-customer-cta">Sign up as a customer</Link>
          </div>
          <div className="brand-card p-8 noise-bg" style={{ background: "var(--brand-green)", color: "#fff" }}>
            <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#FFD8B8" }}>Tradespeople</div>
            <div className="font-display font-extrabold text-5xl mt-1">£3.99<span className="text-base font-medium text-white/80">/month</span></div>
            <div className="text-white/80 text-sm">or £39.99/year (save 16%)</div>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Unlimited job alerts</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> 0–50 mile range slider</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Quote, chat, complete — all inside</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> No commissions, ever</li>
            </ul>
            <Link to="/register?role=tradesperson" className="btn-accent mt-6 inline-flex" data-testid="pricing-trader-cta">Join the trade network</Link>
          </div>
        </div>
        <p className="text-xs text-[var(--text-muted)] text-center mt-8">ℹ️ Standard payment processing fees are retained by the provider and are non-refundable — this keeps our prices low and fair.</p>
      </div>
    </Layout>
  );
}

export function HowItWorks() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-5 py-16">
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl" style={{ color: "var(--brand-green)" }}>How it works</h1>
        <div className="mt-8 space-y-6">
          {[
            { n: "01", t: "Customer posts a job", d: "Select a trade, describe the work, share your UK postcode, optionally add photos, budget and a preferred date." },
            { n: "02", t: "Trades get instant alerts", d: "Matching tradespeople in range receive push + email alerts immediately." },
            { n: "03", t: "Quotes & chat", d: "Trades send formal quotes. Customers review profiles, ratings and chat — all inside the platform." },
            { n: "04", t: "Hire & complete", d: "Accept a quote, arrange the work, and leave a verified review once done." },
          ].map((s) => (
            <div key={s.n} className="brand-card p-6 flex gap-5 items-start">
              <span className="font-display font-extrabold text-3xl" style={{ color: "var(--brand-orange)" }}>{s.n}</span>
              <div>
                <h3 className="font-display font-semibold text-xl" style={{ color: "var(--brand-green)" }}>{s.t}</h3>
                <p className="text-[var(--text-muted)] mt-1">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}

export function Terms() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 py-16 prose prose-slate">
        <h1 className="font-display font-extrabold text-4xl" style={{ color: "var(--brand-green)" }}>Terms of Service</h1>
        <p className="text-[var(--text-muted)] mt-3">Last updated: {new Date().toLocaleDateString()}</p>
        <p className="mt-4">By using MyHandyHub (myhandyhub.co.uk) you agree to these terms. Customers may post jobs free of charge. Tradespeople pay a subscription of £3.99/month (or £39.99/year). Standard payment processing fees retained by the payment provider are non-refundable.</p>
        <h2 className="font-display font-semibold text-2xl mt-6" style={{ color: "var(--brand-green)" }}>Use of the platform</h2>
        <p className="text-[var(--text-muted)]">You agree to provide accurate information, communicate respectfully, and not solicit users off-platform until you have engaged.</p>
        <h2 className="font-display font-semibold text-2xl mt-6" style={{ color: "var(--brand-green)" }}>Liability</h2>
        <p className="text-[var(--text-muted)]">MyHandyHub introduces customers to tradespeople. Work performed is between those parties. We do not guarantee work but we facilitate verified reviews.</p>
      </div>
    </Layout>
  );
}

export function Privacy() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 py-16 prose prose-slate">
        <h1 className="font-display font-extrabold text-4xl" style={{ color: "var(--brand-green)" }}>Privacy Policy</h1>
        <p className="text-[var(--text-muted)] mt-3">Last updated: {new Date().toLocaleDateString()}</p>
        <p className="mt-4">We are GDPR compliant. We store the personal data you provide (name, email, postcode, profile info, job details and messages) to operate the platform.</p>
        <p className="text-[var(--text-muted)] mt-3">You can request deletion of your account and data at any time by emailing hello@myhandyhub.co.uk.</p>
      </div>
    </Layout>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  return (
    <Layout>
      <div className="max-w-md mx-auto px-5 py-16">
        <div className="brand-card p-7">
          <h1 className="font-display font-extrabold text-2xl text-center" style={{ color: "var(--brand-green)" }}>Reset password</h1>
          <p className="text-center text-sm text-[var(--text-muted)] mt-1">Enter your email and we'll send you a reset link.</p>
          {sent ? (
            <p className="text-center mt-5 text-[var(--text-main)]">If an account exists for that email, a reset link has been sent.</p>
          ) : (
            <form onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                const api = (await import("../lib/api")).default;
                await api.post("/auth/forgot-password", { email });
                setSent(true);
              } finally { setBusy(false); }
            }} className="mt-5 space-y-3">
              <input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]" />
              <button disabled={busy} className="btn-primary w-full justify-center" data-testid="forgot-submit-btn">{busy ? "Sending…" : "Send reset link"}</button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
}

export function ResetPassword() {
  const [params] = new URLSearchParams(window.location.search) ? [new URLSearchParams(window.location.search)] : [new URLSearchParams()];
  const token = params.get("token") || "";
  const [password, setPassword] = React.useState("");
  const [done, setDone] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  return (
    <Layout>
      <div className="max-w-md mx-auto px-5 py-16">
        <div className="brand-card p-7">
          <h1 className="font-display font-extrabold text-2xl text-center" style={{ color: "var(--brand-green)" }}>Set a new password</h1>
          {done ? (
            <p className="text-center mt-5"><Link to="/login" className="text-[var(--brand-green)] underline">Back to login</Link></p>
          ) : (
            <form onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                const api = (await import("../lib/api")).default;
                await api.post("/auth/reset-password", { token, password });
                setDone(true);
              } catch {} finally { setBusy(false); }
            }} className="mt-5 space-y-3">
              <input data-testid="reset-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" className="w-full px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]" />
              <button disabled={busy} className="btn-primary w-full justify-center" data-testid="reset-submit-btn">{busy ? "Saving…" : "Reset password"}</button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
}
